import { isFunction } from '../ir/guards';
import { attributeText, safeStyleValue } from '../ir/props';
import { readThrough } from '../ir/values';
import type { DynAttr, EventBinding, EventHandler, Reader } from '../ir/types';
import { getAction } from '../islands/registry';
import { checkNameAfterWrite } from './dev';
import { isHtmlElement } from './nodes';
import { Signal } from '@crown/signal';

const isAttrValue = <T,>(value: T): value is T & (string | number | bigint | boolean | null | undefined) =>
  value === null || value === undefined || (typeof value !== 'object' && typeof value !== 'function' && typeof value !== 'symbol');

export type Disposer = () => void;

/** What a list of bindings holds to undo them: a function, or an effect that disposes itself (one closure fewer). */
export type Teardown = Disposer | { dispose(): void };

/** Reports an error thrown inside a binding effect to the nearest boundary, or to the console. */
export type ErrorSink = (cause: unknown) => void;

const defaultSink: ErrorSink = (cause) => {
  console.error('[@crown/framework] binding failed', cause);
};

/**
 * One effect per binding: it reads its signals and writes the DOM.
 *
 * The effect already runs on the microtask after the writes that dirtied it,
 * so it writes straight away; queueing the write for another microtask cost a
 * closure per change and bought nothing, as no layout is read in between.
 */
function bind<T>(read: Reader<T>, write: (value: T) => void, disposers: Teardown[], sink: ErrorSink): void {
  const node = Signal.subtle.effectNode(() => {
    let value: T;
    try {
      value = read();
    } catch (error) {
      sink(error);
      return;
    }
    write(value);
  });
  if (node) disposers.push(node);
}

function isFormValueElement(
  element: Element,
): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  return 'value' in element && ('form' in element || element.localName === 'select');
}

export function bindDyn(element: Element, dyn: DynAttr, disposers: Teardown[], sink: ErrorSink = defaultSink): void {
  switch (dyn.kind) {
    case 'attr':
      bind(
        dyn.read,
        (given) => {
          const raw = readThrough(given);
          const value = attributeText(dyn.name, isAttrValue(raw) ? raw : String(raw));
          if (value === null) element.removeAttribute(dyn.name);
          else element.setAttribute(dyn.name, value);
          if (dyn.name === 'value' && isFormValueElement(element)) element.value = value ?? '';
          // Once somebody has ticked a box or picked an option, its attribute no longer
          // decides what it shows; the property does, so the binding writes both.
          else if ((dyn.name === 'checked' || dyn.name === 'selected') && dyn.name in element) Reflect.set(element, dyn.name, value !== null);
        },
        disposers,
        sink,
      );
      return;
    case 'class':
      bind(dyn.read, (on) => element.classList.toggle(dyn.name, on), disposers, sink);
      return;
    case 'style':
      bind(
        dyn.read,
        (raw) => {
          if (!isHtmlElement(element)) return;
          const value = safeStyleValue(raw);
          if (value === null) element.style.removeProperty(dyn.name);
          else element.style.setProperty(dyn.name, value);
        },
        disposers,
        sink,
      );
      return;
    case 'prop':
      bind(
        dyn.read,
        (value) => {
          Reflect.set(element, dyn.name, value);
        },
        disposers,
        sink,
      );
  }
}

export function bindText(node: Text, read: Reader<unknown>, disposers: Teardown[], sink: ErrorSink = defaultSink): void {
  bind(
    read,
    (value) => {
      node.data = value === null || value === undefined ? '' : String(value);
      checkNameAfterWrite(node);
    },
    disposers,
    sink,
  );
}

/** Handlers attach directly; action keys resolve through the registry at dispatch time, so late registration still works. */
export function bindEvents(element: Element, on: readonly EventBinding[], disposers: Teardown[]): void {
  for (const binding of on) {
    const listener =
      'handler' in binding
        ? binding.handler
        : (event: Event) => {
            const action = getAction(binding.action);
            if (!action) {
              console.error(`[@crown/framework] no action registered for "${binding.action}"`);
              return;
            }
            if (isHtmlElement(element)) action(element, event);
          };
    element.addEventListener(binding.name, listener, binding.options);
    disposers.push(() => element.removeEventListener(binding.name, listener, binding.options));
  }
}

/** Where an element keeps the handler the shared listener calls for one event type. */
/** Property names per event type, made once: this runs for every handler on every row. */
const handlerKeys = new Map<string, string>();
const handlerKey = (type: string): string => {
  let key = handlerKeys.get(type);
  if (key === undefined) {
    key = `__crownOn${type}`;
    handlerKeys.set(type, key);
  }
  return key;
};

type HandlerHost = Element & Record<string, EventHandler | undefined>;

/**
 * One listener function for every element: it calls the handler the element
 * holds for the event's type. Registering the same function everywhere costs
 * no closure per element, and the event still reaches the element as the
 * browser dispatches it — bubbling or not, in order, with its own `this`.
 */
function sharedListener(this: HandlerHost, event: Event): void {
  const handler = this[handlerKey(event.type)];
  if (handler) handler.call(this, event);
}

/**
 * A handler on an element that only ever leaves the page with its bindings:
 * a fresh template copy. Nothing is kept to undo it, as the element goes.
 * Anything else, or a second handler for the same type, takes `bindEvents`.
 */
export function bindHandler(element: Element, binding: EventBinding, disposers: Teardown[]): void {
  // SAFETY: the handler slots are expando properties under keys only
  // handlerKey makes, so they never meet a property the element has.
  const host = element as HandlerHost;
  const key = handlerKey(binding.name);
  if (!('handler' in binding) || binding.options || host[key]) {
    bindEvents(element, [binding], disposers);
    return;
  }
  host[key] = binding.handler;
  element.addEventListener(binding.name, sharedListener);
}

export function disposeAll(disposers: Teardown[]): void {
  for (let i = disposers.length - 1; i >= 0; i--) {
    const dispose = disposers[i];
    if (isFunction(dispose)) dispose();

    else if (dispose) dispose.dispose();
  }
  disposers.length = 0;
}
